import type { AdminPageSeo, ContentType, DeleteResult } from '@apuestapr/contracts';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ApiClient, ApiFailure, ApiResult } from '../api/client.ts';
import {
  accessFor,
  useRequest,
  type MutationState,
  type RequestState,
} from '../api/request-state.ts';
import { createContentApi } from './api.ts';
import type {
  ContentCreateByType,
  ContentItemByType,
  ContentPreviewByType,
  ContentStatusByType,
  ContentUpdateByType,
} from './catalog.ts';

/**
 * Re-exported rather than defined here since Task 13.1.
 *
 * None of these is about content — a loading/ready/error triple, the 403-versus-fault classification, and a
 * form's field errors are the same on any authenticated screen, and the support queue needs all three. They
 * live in `api/request-state.ts` now; this keeps every existing import and test in this directory working.
 */
export {
  fieldErrorsFor,
  useApiMutation,
  useRequest,
  type MutationState,
  type RequestState,
} from '../api/request-state.ts';

export const useContentList = <Type extends ContentType>(
  client: ApiClient,
  type: Type,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<readonly ContentItemByType[Type][]> => {
  const api = useMemo(() => createContentApi(client), [client]);
  const load = useCallback(() => api.list(type), [api, type]);
  return useRequest(load, onFailure);
};

export const useContentDetail = <Type extends ContentType>(
  client: ApiClient,
  type: Type,
  id: number,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<ContentItemByType[Type]> => {
  const api = useMemo(() => createContentApi(client), [client]);
  const load = useCallback(() => api.detail(type, id), [api, id, type]);
  return useRequest(load, onFailure);
};

export const useContentPreview = <Type extends ContentType>(
  client: ApiClient,
  type: Type,
  id: number,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<ContentPreviewByType[Type]> => {
  const api = useMemo(() => createContentApi(client), [client]);
  const load = useCallback(() => api.preview(type, id), [api, id, type]);
  return useRequest(load, onFailure);
};

export const usePageSeoList = (
  client: ApiClient,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<readonly AdminPageSeo[]> => {
  const api = useMemo(() => createContentApi(client), [client]);
  const load = useCallback(() => api.listPageSeo(), [api]);
  return useRequest(load, onFailure);
};

export const usePageSeoDetail = (
  client: ApiClient,
  id: number,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<AdminPageSeo> => {
  const api = useMemo(() => createContentApi(client), [client]);
  const load = useCallback(() => api.pageSeoDetail(id), [api, id]);
  return useRequest(load, onFailure);
};

export interface ManagedContentList<Type extends ContentType> {
  readonly state: RequestState<readonly ContentItemByType[Type][]>;
  readonly mutation: MutationState<ContentItemByType[Type] | DeleteResult>;
  readonly reconciling: boolean;
  readonly reconciliationFailure: ApiFailure | null;
  readonly create: (
    input: ContentCreateByType[Type],
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly update: (
    id: number,
    input: ContentUpdateByType[Type],
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly changeStatus: (
    id: number,
    input: ContentStatusByType[Type],
  ) => Promise<ApiResult<ContentItemByType[Type]>>;
  readonly remove: (id: number) => Promise<ApiResult<DeleteResult>>;
  readonly reconcile: () => Promise<void>;
}

/**
 * Owns the list's in-memory mutation behavior. Successful writes patch the current server-ordered
 * array immediately, then a background GET replaces it with a fresh database-clock projection.
 */
export const useManagedContentList = <Type extends ContentType>(
  client: ApiClient,
  type: Type,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedContentList<Type> => {
  const api = useMemo(() => createContentApi(client), [client]);
  const [state, setState] = useState<RequestState<readonly ContentItemByType[Type][]>>({
    status: 'loading',
  });
  const [mutation, setMutation] = useState<MutationState<ContentItemByType[Type] | DeleteResult>>({
    status: 'ready',
  });
  const [reconciling, setReconciling] = useState(false);
  const [reconciliationFailure, setReconciliationFailure] = useState<ApiFailure | null>(null);
  const mounted = useRef(true);
  const reconciliationGeneration = useRef(0);
  const activeType = useRef<ContentType>(type);

  useLayoutEffect(() => {
    activeType.current = type;
  }, [type]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    const generation = reconciliationGeneration.current + 1;
    reconciliationGeneration.current = generation;
    setReconciliationFailure(null);
    setReconciling(true);
    const result = await api.list(type);
    // A newer reconciliation owns the screen; an older response must never move it backward.
    if (
      !mounted.current ||
      activeType.current !== type ||
      generation !== reconciliationGeneration.current
    )
      return;
    setReconciling(false);
    if (result.ok) {
      // This assignment is deliberately direct: API order is the display order.
      setState({ status: 'ready', data: result.data });
    } else {
      setReconciliationFailure(result.failure);
      onFailure?.(result.failure);
    }
  }, [api, onFailure, type]);

  useEffect(() => {
    let active = true;
    reconciliationGeneration.current += 1;
    queueMicrotask(() => {
      if (active) {
        setState({ status: 'loading' });
        setMutation({ status: 'ready' });
        setReconciling(false);
        setReconciliationFailure(null);
      }
    });
    void api.list(type).then((result) => {
      if (!active) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
    return () => {
      active = false;
    };
  }, [api, onFailure, type]);

  const runMutation = useCallback(
    async <Value>(
      operation: () => Promise<ApiResult<Value>>,
      apply: (
        rows: readonly ContentItemByType[Type][],
        value: Value,
      ) => readonly ContentItemByType[Type][],
    ): Promise<ApiResult<Value>> => {
      setMutation({ status: 'loading' });
      const result = await operation();
      if (!mounted.current || activeType.current !== type) return result;
      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }

      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', data: apply(current.data, result.data) }
          : current,
      );
      setMutation({
        status: 'ready',
        data: result.data as ContentItemByType[Type] | DeleteResult,
      });
      void reconcile();
      return result;
    },
    [onFailure, reconcile, type],
  );

  const create = useCallback(
    (input: ContentCreateByType[Type]) =>
      runMutation(
        () => api.create(type, input, csrfToken),
        (rows, row) => [...rows, row],
      ),
    [api, csrfToken, runMutation, type],
  );
  const update = useCallback(
    (id: number, input: ContentUpdateByType[Type]) =>
      runMutation(
        () => api.update(type, id, input, csrfToken),
        (rows, row) => rows.map((current) => (current.id === row.id ? row : current)),
      ),
    [api, csrfToken, runMutation, type],
  );
  const changeStatus = useCallback(
    (id: number, input: ContentStatusByType[Type]) =>
      runMutation(
        () => api.changeStatus(type, id, input, csrfToken),
        (rows, row) => rows.map((current) => (current.id === row.id ? row : current)),
      ),
    [api, csrfToken, runMutation, type],
  );
  const remove = useCallback(
    (id: number) =>
      runMutation(
        () => api.remove(type, id, csrfToken),
        (rows) => rows.filter((row) => row.id !== id),
      ),
    [api, csrfToken, runMutation, type],
  );

  return {
    state,
    mutation,
    reconciling,
    reconciliationFailure,
    create,
    update,
    changeStatus,
    remove,
    reconcile,
  };
};
